import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "../../../components/form/select/Select";
import RiskCovered from "../../../components/modals/policy/RiskCovered";
import PageTitle from "../../../components/partials/PageTitle";
import { BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  allActivePolicies,
  attachPolicy,
  deleteRiskCover,
  getAllActivePolicies,
  getPolicy,
  // resetActivePolicies,
  resetPolicyStatus,
  resetRiskCover,
} from "../../../redux/slice/policy/policySlice";
import { removeLeadingZeros } from "../../../utils/helpers/filter";
import { categories } from "../../../utils/data/account_type";
import moment, { duration } from "moment/moment";
import { useLocation, useNavigate } from "react-router-dom";
import { policyApplication } from "../../../utils/validators/schema";
import { flashMessage } from "../../../utils/flash";
import { policyValidation } from "../../../utils/validators/validators";
import { getSingleCustomer } from "../../../redux/slice/customer/customerSlice";
import { getAllCovers } from "../../../redux/slice/cover/coverSlice";
import { calculateAnnualPremium } from "../../../utils/helpers/functions";
import { FileUploader } from "react-drag-drop-files";
import useFileUpload from "../../../utils/hooks/useFileUpload";

const PolicyApplicationSettings = () => {
  const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
  const activePolicies = useSelector(allActivePolicies);
  const {
    single_policy,
    risk_covered,
    isLoading,
    temp_policy_attach_data,
    isUpdated,
  } = useSelector((state) => state.policy);
  const { user } = useSelector((state) => state?.auth?.user);
  const { user_permissions } = useSelector((state) => state?.auth?.user);
  const { single_customer } = useSelector((state) => state?.customer);
  const { all_covers } = useSelector((state) => state?.cover);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state: cus_state } = useLocation();

  const { state, id } = cus_state;

  const {
    fileUrl,
    loading: uploadingProposalDoc,
    singleFileUpload,
  } = useFileUpload();
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [butCharge, setButCharge] = useState(
    temp_policy_attach_data?.but_charge ? true : false
  );
  const [riskCovered, setRiskCovered] = useState(risk_covered);
  const [editData, setEditData] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [policyId, setPolicyId] = useState(null);
  const [policyIdError, setPolicyIdError] = useState({
    error: "",
    touched: false,
  });
  const [riskError, setRiskError] = useState("");
  const [applyTo, setApplyTo] = useState(() => {
    let applyTo;
    if (temp_policy_attach_data) {
      if (temp_policy_attach_data?.repayment_frequency === "one-off") {
        applyTo = "OO";
      } else {
        applyTo = "INS";
      }
    } else {
      applyTo = "OO";
    }
    return applyTo;
  });

  const breadcrumb = {
    from: {
      title: "policy",
      link: "/admin/policy-application",
    },
    current: {
      title: "policy application",
      link: null,
    },
  };

  const onSubmit = (values) => {
    let policyIdCopy = temp_policy_attach_data?.policy_id
      ? temp_policy_attach_data?.policy_id
      : policyId;

    if (!policyIdCopy) {
      setPolicyIdError({
        error: "Policy is required",
        touched: true,
      });
      return;
    }

    if (riskCovered?.length <= 0) {
      flashMessage("error", "Atleast one risk cover must be provided");
      return;
    }

    // submitting data
    dispatch(
      attachPolicy({
        ...values,
        end_date: endDate,
        staff_id: user.staff_id,
        customer_age: state
          ? state?.basic_information?.age
          : single_customer?.basic_information?.age,
        customer_id:
          state?.basic_information.customer_id ||
          single_customer?.basic_information?.customer_id,
        office_name:
          state?.basic_information.office_name ||
          single_customer?.basic_information?.office_name,
        customer_name: state
          ? state?.basic_information.first_name +
            " " +
            state?.basic_information.last_name
          : single_customer?.basic_information.first_name +
            " " +
            single_customer?.basic_information.last_name,
        agent_name: state
          ? state?.basic_information?.user_first_name +
            " " +
            state?.basic_information?.user_last_name
          : single_customer?.basic_information?.user_first_name +
            " " +
            single_customer?.basic_information.user_last_name,
        policy_id: policyIdCopy,
        risk_covered: riskCovered,
        proposal_document:
          fileUrl || temp_policy_attach_data?.proposal_document,
      })
    );
    setIsSubmitted(true);
  };

  const handleFileChange = (file) => {
    setIsUploading(true);
    singleFileUpload(file);
  };

  // form control and validations
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      duration:
        temp_policy_attach_data?.duration ||
        single_policy?.data?.p_gen_info?.minimun_duration,
      effective_date: moment(new Date()).format("YYYY-MM-DD"),
      repayment_frequency: temp_policy_attach_data?.repayment_frequency
        ? temp_policy_attach_data?.repayment_frequency
        : "one-off",
      discount: temp_policy_attach_data?.discount || "",
      but_charge: temp_policy_attach_data?.but_charge || "",
      total_annual_premium: null,
    },
    validationSchema: policyApplication,
    validate: (values) =>
      policyValidation(
        values,
        single_policy?.data?.p_gen_info?.minimun_duration,
        butCharge
      ),
    onSubmit,
  });

  // date manipulation starts here
  const calculateEndDate = () => {
    const effectiveDateObj = moment(values?.effective_date);
    const EndDateObj = effectiveDateObj
      .add(values?.duration, "months")
      .subtract(1, "days"); // Subtract 1 day
    const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");
    setEndDate(EndDateFormatted);
  };

  // calculate total premium
  const totalPremium = useMemo(() => {
    let totalPremium = 0;
    if (riskCovered?.length > 0) {
      let total = riskCovered?.reduce((acc, totalPremium) => {
        // let calc = totalPremium?.sum_insured * totalPremium.rate * 0.01;
        return (acc += totalPremium?.annual_premium);
      }, 0);
      totalPremium = total;
    }
    if (values.discount) {
      totalPremium -= totalPremium * values.discount * 0.01;
    }
    setFieldValue("total_annual_premium", totalPremium);

    return totalPremium;
  }, [values.discount, riskCovered]);

  useEffect(() => {
    const risk = risk_covered?.map((data) => {
      let payload = {
        ...data,
        annual_premium: calculateAnnualPremium(
          values?.effective_date,
          endDate,
          data?.rate,
          data?.sum_insured
        ),
      };
      return payload;
    });

    setRiskCovered(risk);
  }, [endDate]);

  useEffect(() => {
    if (id) {
      dispatch(getSingleCustomer({ id, office_id: user.office_id }));
    }
  }, [dispatch, id, user.office_id]);

  useEffect(() => {
    if (values?.effective_date || values?.duration) {
      calculateEndDate();
    }
  }, [values?.effective_date, values?.duration]);

  useEffect(() => {
    if (!temp_policy_attach_data) {
      dispatch(getAllActivePolicies({ office_id: user.office_id }));
      dispatch(resetPolicyStatus());
    }
    dispatch(getAllCovers());
  }, []);

  useEffect(() => {
    if (policyId) {
      const payload = {
        id: policyId,
        office_id: user?.office_id,
      };
      dispatch(resetRiskCover());
      dispatch(getPolicy(payload));
    }
  }, [policyId, isUpdated]);

  useEffect(() => {
    setRiskCovered(risk_covered);
    setRiskError("");
  }, [risk_covered]);

  useEffect(() => {
    if (isSubmitted) {
      navigate(`/admin/policy-application/overview`);
    }
  }, [isSubmitted, navigate]);

  useEffect(() => {
    if (
      !user_permissions?.some((permission) => permission.name === "customer module") && !user_permissions?.some((permission) => permission.name === "attach policy - individual")
    ) {
      navigate("/admin/dashboard");
    }
  }, [navigate, state?.basic_information, user_permissions]);

  return (
    <>
      {/* page title */}
      <PageTitle title='Policy Application' breadcrumb={breadcrumb} />
      {/* end page title */}

      <div className='row'>
        <div className='col-md-12'>
          <div className='card'>
            <div className='card-body'>
              <div>
                <div className='row'>
                  <div className='col-md-12'>
                    <div className='mb-3'>
                      <label htmlFor='policy_id' className='form-label'>
                        Select Policy <span className='text-danger'>*</span>
                      </label>
                      <Select
                        onBlur={() => {
                          setPolicyIdError({ ...policyIdError, touched: true });
                          if (!policyId) {
                            setPolicyIdError({
                              error: "Policy is required",
                              touched: true,
                            });
                          }
                        }}
                        name='policy_id'
                        id='policy_id'
                        data={activePolicies?.policy?.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))}
                        value={policyId || temp_policy_attach_data?.policy_id}
                        onChange={(selectedOption) =>
                          setPolicyId(selectedOption?.value)
                        }
                        isError={!policyId && policyIdError?.touched}
                      />
                      {!policyId && policyIdError?.touched && (
                        <p className='mb-0 text-danger'>
                          {policyIdError?.error + "*"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='col-md-12 mb-3'>
                    {(policyId || temp_policy_attach_data?.policy_id) &&
                      (!isLoading ? (
                        <div className='col-md-4'>
                          <span
                            className='btn btn-primary text-white px-4'
                            data-bs-toggle='modal'
                            data-bs-target='#more_document_modal'
                          >
                            Add Risk Covered
                          </span>
                        </div>
                      ) : null)}
                  </div>
                  <form onSubmit={handleSubmit} className='row'>
                    {/* risk covered table */}
                    {riskCovered?.length > 0 ? (
                      <div className='col-md-12 mb-3 mx-2 table-responsive-sm'>
                        <table className='table table-centered table-striped nowrap w-100 no-footer'>
                          <thead>
                            <tr>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "130.8px" }}
                                aria-label='Customer: activate to sort column ascending'
                              >
                                Policy Cover Type
                              </th>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "140.8px" }}
                                aria-label='Customer: activate to sort column ascending'
                              >
                                Cover For
                              </th>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "108.8px" }}
                                aria-label='Phone: activate to sort column ascending'
                              >
                                Rate(%)
                              </th>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "147.8px" }}
                              >
                                Sum Insured
                              </th>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "147.8px" }}
                                aria-label='Email: activate to sort column ascending'
                              >
                                Annual Premium
                              </th>
                              <th
                                className='sorting'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "246.8px" }}
                                aria-label='Location: activate to sort column ascending'
                              >
                                Detail Description / Specification
                              </th>
                              <th
                                className='sorting sorting_asc'
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "146.8px" }}
                              ></th>
                            </tr>
                          </thead>

                          <tbody>
                            {riskCovered?.map((item, i) => (
                              <tr key={i}>
                                <td>
                                  {
                                    all_covers?.unpaginated_cover?.filter(
                                      (p) => p.id === item?.cover_type
                                    )[0]?.name
                                  }
                                </td>
                                <td>{item?.cover_for}</td>
                                <td className='table-user'>{item?.rate}</td>
                                <td className='table-user'>
                                  {item?.sum_insured}
                                </td>
                                <td className='text-dark'>
                                  {item?.annual_premium?.toFixed(2)}
                                </td>
                                <td>{item?.description}</td>

                                <td className='text-center'>
                                  <div className='d-flex gap-2 align-content-center justify-content-center'>
                                    <span
                                      className='text-primary cursor-p'
                                      data-bs-toggle='modal'
                                      data-bs-target='#more_document_modal'
                                      onClick={() => setEditData(item)}
                                    >
                                      <BsPencilFill size={17} />
                                    </span>
                                    <span
                                      className='text-danger cursor-p'
                                      onClick={() =>
                                        dispatch(deleteRiskCover(item?.id))
                                      }
                                    >
                                      <ImBin size={17} />
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {riskError && (
                      <div>
                        <h6 className='alert alert-danger mb-4'>{riskError}</h6>
                      </div>
                    )}
                    {/* end risk covered table */}
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label htmlFor='policy_name' className='form-label'>
                          Duration (in months){" "}
                          <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='number'
                          id='duration'
                          name='duration'
                          className={`form-control form-control-sm no-arrows`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values?.duration}
                          onInput={(e) => removeLeadingZeros(e.target.id)}
                          // placeholder='Enter Duration'
                        />
                        {errors?.duration && touched?.duration && (
                          <span className='text-danger'>
                            {errors?.duration}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label htmlFor='policy_name' className='form-label'>
                          Effective date <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='date'
                          id='effective_date'
                          name='effective_date'
                          className={`form-control form-control-sm`}
                          value={values?.effective_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors?.effective_date && touched?.effective_date && (
                          <span className='text-danger'>
                            {errors?.effective_date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label htmlFor='policy_name' className='form-label'>
                          End date
                        </label>
                        <input
                          type='date'
                          id='end_date'
                          name='end_date'
                          defaultValue={endDate}
                          className={`form-control form-control-sm`}
                          disabled
                        />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label className='form-label'>
                          Repayment Frequency{" "}
                          <span className='text-danger'>*</span>
                        </label>
                        <div className='form-check my-1'>
                          <input
                            className='form-check-input me-2'
                            type='radio'
                            name='apply_to'
                            id='apply_to'
                            onChange={(e) => {
                              setApplyTo(e.target.value);
                              setFieldValue("repayment_frequency", "one-off");
                            }}
                            defaultChecked={applyTo === "OO"}
                          />
                          <label
                            className='form-check-label'
                            htmlFor='apply_to'
                          >
                            One-off
                          </label>
                        </div>
                        <div className='form-check'>
                          <input
                            className='form-check-input me-2'
                            type='radio'
                            name='apply_to'
                            id='apply_to'
                            defaultChecked={applyTo === "INS"}
                            onChange={() => {
                              setApplyTo("INS");
                              setFieldValue("repayment_frequency", "");
                              setFieldTouched("repayment_frequency", false);
                            }}
                          />
                          <label
                            className='form-check-label mb-1'
                            htmlFor='repayment_frequency'
                          >
                            Installments
                          </label>
                          {applyTo === "INS" && (
                            <div>
                              <Select
                                isError={
                                  errors?.repayment_frequency &&
                                  touched?.repayment_frequency
                                }
                                name='repayment_frequency'
                                // ref={repayment_frequencyRef}
                                data={categories?.map((item) => ({
                                  label: item?.name,
                                  value: item?.name,
                                }))}
                                value={values.repayment_frequency}
                                onChange={(selectedOption) =>
                                  setFieldValue(
                                    "repayment_frequency",
                                    selectedOption?.value
                                  )
                                }
                                onBlur={() => {
                                  handleBlur({
                                    target: { name: "repayment_frequency" },
                                  });
                                }}
                              />
                              {errors?.repayment_frequency &&
                                touched?.repayment_frequency && (
                                  <p className='text-danger mb-0'>
                                    {errors?.repayment_frequency}
                                  </p>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label htmlFor='policy_name' className='form-label'>
                          Discount(%)
                        </label>
                        <input
                          type='number'
                          id='discount'
                          name='discount'
                          value={values?.discount}
                          className={`form-control form-control-sm no-arrows`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors?.discount && touched?.discount && (
                          <span className='text-danger'>
                            {errors?.discount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label
                          htmlFor='but_charge'
                          className='form-label d-flex gap-1 align-content-center'
                        >
                          <input
                            className={`form-check-input form-check-input-sm`}
                            type='checkbox'
                            id='but_charge'
                            onChange={() => {
                              setButCharge(!butCharge);
                              setFieldValue("but_charge", null);
                            }}
                            style={{ height: "16px", width: "16px" }}
                            defaultChecked={temp_policy_attach_data?.but_charge}
                          />
                          But-Charge
                        </label>
                        {butCharge && (
                          <input
                            type='number'
                            id='but_charge'
                            name='but_charge'
                            className={`form-control form-control-sm no-arrows`}
                            value={values?.but_charge}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onInput={(e) => removeLeadingZeros(e.target.id)}
                          />
                        )}
                        {errors?.but_charge &&
                          touched?.but_charge &&
                          butCharge && (
                            <span className='text-danger'>
                              {errors?.but_charge}
                            </span>
                          )}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label htmlFor='policy_name' className='form-label'>
                          Total Annual Premium
                        </label>
                        <input
                          type='number'
                          id='total_premium'
                          name='total_premium'
                          className={`form-control form-control-sm`}
                          value={
                            totalPremium > 0 ? totalPremium?.toFixed(2) : 0
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='mb-3'>
                        <label
                          className='form-label d-block'
                          htmlFor='proposal_document'
                        >
                          <span
                            className={`${
                              (fileUrl ||
                                temp_policy_attach_data?.proposal_document) &&
                              !uploadingProposalDoc &&
                              "text-success"
                            }`}
                          >
                            Upload Proposal document
                          </span>
                          {!fileUrl && uploadingProposalDoc && (
                            <span>
                              <i className='mdi mdi-dots-circle mdi-spin '></i>
                            </span>
                          )}
                          {(fileUrl ||
                            temp_policy_attach_data?.proposal_document) &&
                            !uploadingProposalDoc && (
                              <span>
                                <i className='mdi mdi-check text-success fs-5'></i>
                              </span>
                            )}
                        </label>
                        <FileUploader
                          label='Browse Document'
                          children={
                            <div
                              className='borde border-dotted p-1 px-2 text-center cursor-p'
                              style={{ border: "2px dashed grey" }}
                            >
                              <i className='uil uil-cloud-upload fs-4'></i>{" "}
                              Upload Document
                              <span className='text-primary-dark mt-0'></span>
                            </div>
                          }
                          handleChange={handleFileChange}
                          onDrop={handleFileChange}
                          name='file'
                          types={fileTypes}
                        />
                        {temp_policy_attach_data?.proposal_document ? (
                          <a
                            href={
                              fileUrl ||
                              temp_policy_attach_data?.proposal_document
                            }
                            target='_blank'
                            className='btn btn-sm btn-primary text-white align-items-center align-content-center justify-content-center ms-2'
                          >
                            <i className='mdi mdi-eye text-white fs-5'></i>
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <div className='d-flex justify-content-end pt-5'>
                      {/* <button
                                    type='button'
                                    className='btn btn-info text-white me-3'
                                    onClick={() => accountingRef?.current?.click()}
                                >
                                    <i className='mdi mdi-arrow-left me-1'></i>
                                    Previous
                                </button> */}
                      <button
                        type='submit'
                        className='btn btn-primary text-white px-4'
                      >
                        Next
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RiskCovered
        modal_id='more_document_modal'
        editData={editData}
        setEditData={setEditData}
        dateData={{ effectiveDate: values?.effective_date, endDate }}
        rate={values?.rate}
      />
    </>
  );
};

export default PolicyApplicationSettings;
